/**
 * The More ▸ DBA group's engine-neutral and MSSQL actions — Session Browser,
 * Filegroup Manager, Agent Jobs, Instance Monitor.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11. Oracle-only DBA actions
 * live in `useToolbarOracleActions`.
 */
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import {
  hasJobSchedulerSupport,
  getJobSchedulerToolbarLabel,
} from '../../workspace/job-scheduler/engineConfig';
import { supportsMssqlDbaEngineFilter } from '../model/toolbarEngineFilters';
import { openQueryStoreTab } from '../../workspace/query-store/openQueryStoreTab';
import type { ToolbarActionContext } from './toolbarActionContext';

export function useToolbarDbaActions(
  ctx: ToolbarActionContext,
  selectedConnectionId: string | null | undefined,
) {
  const { track, requestContext, addTab, activeConnections } = ctx;

  const handleOpenSessionBrowser = async () => {
    addBreadcrumb('click', 'toolbar:session-browser');
    track('toolbar:session_browser');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false },
      'Session Browser'
    );
    if (!context) return;

    addTab({
      title: `Sessions: ${context.connectionName}`,
      type: 'session-browser',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        filter: 'active',
        autoRefreshInterval: 0,
        isLoading: true,
      },
    });
  };

  const handleOpenFilegroupManager = async () => {
    addBreadcrumb('click', 'toolbar:filegroup-manager');
    track('toolbar:filegroup_manager');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsMssqlDbaEngineFilter },
      'Filegroup Manager'
    );
    if (!context) return;

    addTab({
      title: `Filegroups: ${context.connectionName}`,
      type: 'filegroup-manager',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        isLoading: true,
      },
    });
  };

  const handleOpenQueryStore = async () => {
    addBreadcrumb('click', 'toolbar:query-store');
    track('toolbar:query_store');
    // Database-scoped: the existing context picker asks for the database when
    // the tree selection does not already pin one.
    const context = await requestContext(
      { requireConnection: true, requireDatabase: true, engineFilter: supportsMssqlDbaEngineFilter },
      'Query Store'
    );
    if (!context?.database) return;
    openQueryStoreTab({
      entry: 'toolbar',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
        database: context.database,
      },
    });
  };

  const handleOpenAgentJobs = async () => {
    addBreadcrumb('click', 'toolbar:agent-jobs');
    track('toolbar:agent_jobs');
    const label = getJobSchedulerToolbarLabel(
      activeConnections.find((c) => c.id === selectedConnectionId)?.connection?.engine,
    );
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: hasJobSchedulerSupport },
      label
    );
    if (!context) return;
    const tabLabel = getJobSchedulerToolbarLabel(context.engine);

    addTab({
      title: `${tabLabel}: ${context.connectionName}`,
      type: 'job-scheduler',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        autoRefreshInterval: 0,
        statusFilter: 'all',
        isLoading: true,
      },
    });
  };

  const handleOpenInstanceMonitor = async () => {
    addBreadcrumb('click', 'toolbar:instance-monitor');
    track('toolbar:instance_monitor');
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false },
      'Instance Monitor'
    );
    if (!context) return;

    addTab({
      title: `Instance: ${context.connectionName}`,
      type: 'instance-monitor',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
      },
      content: {
        activePanel: 'dashboard',
        autoRefreshInterval: 0,
        isLoading: true,
      },
    });
  };

  return {
    'session-browser': () => void handleOpenSessionBrowser(),
    'filegroup-manager': () => void handleOpenFilegroupManager(),
    'query-store': () => void handleOpenQueryStore(),
    'agent-jobs': () => void handleOpenAgentJobs(),
    'instance-monitor': () => void handleOpenInstanceMonitor(),
  };
}
